import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import type { GlobalBasicSearchQueryInterface } from '@zaoblako/shared/common/dtos/search/global.basic.search.query.dto';
import type { GlobalBasicSearchResultInterface } from '@zaoblako/shared/common/dtos/search/global.basic.search.result.dto';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { SearchService } from '@zaoblako/web/core/services/search.service';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { HighlightPipe } from '@zaoblako/web/shared/pipes/highlight.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { SearchTruncatePipe } from '@zaoblako/web/shared/pipes/search-truncate.pipe';
import { ListboxModule } from 'primeng/listbox';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, of, Subject, tap } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { Key } from 'ts-key-enum';

@Component({
  standalone: true,
  selector: 'crm-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  imports: [
    CommonModule,
    OverlayPanelModule,
    ListboxModule,
    InputTextComponent,
    FormsModule,
    TooltipModule,
    HighlightPipe,
    SafeHtmlPipe,
    SearchTruncatePipe,
    RouterModule
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalSearchComponent implements OnInit {
  @ViewChild('searchInput', { static: false }) public searchInput: InputTextComponent;
  @ViewChild('searchWindow', { static: false }) public searchWindow: ElementRef;

  public loading = false;
  public search = new Subject<string>();
  public searchResults$: Observable<GlobalBasicSearchResultInterface>;

  public queryParams: GlobalBasicSearchQueryInterface = {
    deal: false,
    project: false,
    hd: false,
    task: false,
    user: false,
    company: false,
    contragent: false,
    q: ''
  };

  public readonly filterSearchInfo: { name: string; label: string; accessName: string }[] = [
    { name: 'deal', label: 'Сделки', accessName: 'FINANCE' },
    { name: 'project', label: 'Задачи', accessName: 'PROJECT' },
    { name: 'hd', label: 'HelpDesk', accessName: 'HELPDESK' },
    { name: 'task', label: 'Платежи', accessName: 'FINANCE' },
    { name: 'user', label: 'Пользователи', accessName: 'BASIS' },
    { name: 'company', label: 'Компании', accessName: 'BASIS' },
    { name: 'contragent', label: 'Контрагенты', accessName: 'BASIS' }
  ];

  public filterSearch: { name: string; label: string; accessName: string }[] = [];

  public selectedFilterSearch: { name: string; label: string; accessName: string }[] = [];

  public constructor(
    private readonly searchService: SearchService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.globalService
      .select((state) => state.tenantInfo)
      .subscribe((tenantInfo) => {
        this.filterSearch = [];
        this.selectedFilterSearch = [];

        this.initQueryParams(tenantInfo?.ratePlan?.availableModules || []);

        const listAccessName: string[] = tenantInfo?.ratePlan?.availableModules || [];

        this.filterSearchInfo.forEach((item) => {
          if (listAccessName.includes(item.accessName)) {
            this.filterSearch.push(item);

            if (this.queryParams[item.name]) {
              this.selectedFilterSearch.push(item);
            }
          }
        });
        this.cdRef.markForCheck();
      });

    this.searchResults$ = this.search.pipe(
      map((query) => query?.trim()),
      debounceTime(300),
      switchMap((query) => {
        if (!query || query === '' || query.length < 2) {
          return of(null);
        }
        this.loading = true;
        this.cdRef.markForCheck();
        return this.searchService.search(this.queryParams);
      }),
      tap(() => {
        this.loading = false;
        this.cdRef.markForCheck();
      })
    );

    document.addEventListener('click', (event: MouseEvent) => {
      if (this.searchWindow) {
        if (!(this.searchWindow.nativeElement as HTMLInputElement).contains(event.target as any)) {
          this.search.next('');
          (this.searchInput.input.nativeElement as HTMLInputElement).blur();
        }
      }
    });
  }

  public changeFilterSearch(selectedFilter: { name: string; label: string; accessName: string }[]) {
    this.filterSearch.forEach((item) => {
      this.queryParams[item.name] = selectedFilter.findIndex((f) => f.name === item.name) !== -1;
      localStorage.setItem(`global.search.${item.name}`, String(this.queryParams[item.name]));
      this.search.next(this.queryParams.q);
    });
  }

  public closeSearchWindow() {
    this.search.next('');
  }

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    if (event.key === Key.Escape) {
      this.search.next('');
      (this.searchInput.input.nativeElement as HTMLInputElement).blur();
    }
  }

  private initQueryParams(modules: string[]) {
    this.filterSearchInfo.forEach((item) => {
      if (modules.includes(item.accessName)) {
        this.queryParams[item.name] = localStorage.getItem(`global.search.${item.name}`)
          ? localStorage.getItem(`global.search.${item.name}`) === 'true'
          : true;
      } else {
        this.queryParams[item.name] = false;
      }
    });
  }
}
